import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface CategoryOption {
  readonly id: string;
  readonly name: string;
}

/**
 * GR L27.03 — Knowledge Authoring (`/kb/author`). The authoring form that
 * creates a new `kb_articles` record with its first `kb_article_versions`
 * body. Enforces the §M field rules client-side: title required 3–200 chars,
 * category chosen from `kb_categories`, body required. On success shows the
 * new article code so the author can jump to review.
 */
export function GrKbAuthorPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [body, setBody] = useState('');
  const [validation, setValidation] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: CategoryOption[] }>('/v1/collab/knowledge/categories', { token })
      .then((r) => { if (alive) setCategories(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  function validate(): string | null {
    if (title.trim().length < 3 || title.trim().length > 200) return t('gr27.kbAuthor.validTitle');
    if (!category) return t('gr27.kbAuthor.validCategory');
    if (body.trim().length === 0) return t('gr27.kbAuthor.validBody');
    return null;
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const v = validate();
    if (v) { setValidation(v); return; }
    setValidation(null);
    setSubmitting(true);
    try {
      const r = await apiRequest<{ data: { articleCode: string } }>('/v1/collab/knowledge', {
        token, method: 'POST', body: { title: title.trim(), category, body: body.trim() },
      });
      setCreated(r.data.articleCode);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main aria-label="kb-author">
      <h1>{t('gr27.kbAuthor.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {created && <p aria-label="created">{t('gr27.kbAuthor.created', { code: created })}</p>}
      {validation && <p aria-label="validation" role="status">{validation}</p>}
      <form onSubmit={submit}>
        <label>
          {t('gr27.kbAuthor.labelTitle')}
          <input aria-label="input-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label>
          {t('gr27.kbAuthor.labelCategory')}
          <select aria-label="input-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">{t('gr27.kbAuthor.select')}</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label>
          {t('gr27.kbAuthor.labelBody')}
          <textarea aria-label="input-body" value={body} onChange={(e) => setBody(e.target.value)} />
        </label>
        <button type="submit" aria-label="submit-article" disabled={submitting}>
          {submitting ? t('gr27.kbAuthor.submitting') : t('gr27.kbAuthor.submit')}
        </button>
      </form>
    </main>
  );
}
